/* eslint-disable no-shadow */
/* eslint-disable prefer-promise-reject-errors */
import map from 'lodash/map';
import difference from 'lodash/difference';
import userService from './user/user-service';
import { CookieNames } from './utils/cookies';
import { platformConstants } from '../../components/sso/components/utils/constants/platform-constants';
import { siteConstants } from '../../components/sso/components/utils/constants/sites-constants';

export const THROTTLE_ERROR_MESSAGE = 'Too many requests. Please try again later.';
export const THROTTLE_MESSAGE = 'Because of high traffic, your request takes longer than expected.';
export const THROTTLE_TIME_INTERVAL = 5000;
export const THROTTLE_RETRY_ATTEMPTS = 3;

export const generateBemCss = (bemName, bemList) => {
    let bemCss = '';

    if (Array.isArray(bemList)) {
        bemCss = bemList.map((bemModifier) => `${bemName}--${bemModifier}`).join(' ');
    }

    return `${bemName} ${bemCss}`;
};

export const throttle = (func, timeInterval, count) => {
    return new Promise((resolve, reject) => {
        const throttleLimitReached = count <= 0;
        if (throttleLimitReached) {
            reject({ code: 429, value: THROTTLE_ERROR_MESSAGE });
            return;
        }

        func().then(
            (response) => {
                resolve(response);
            },
            (error) => {
                if (error && error.response && error.response.status === 429) {
                    setTimeout(() => throttle(func, timeInterval, count - 1)
                        .then((response) => resolve(response), (error) => reject(error)), timeInterval);
                    return;
                }
                reject(error);
            }
        );
    });
};

// https://github.com/facebook/react/issues/5465
export const makeCancelable = (promise) => {
    let hasCanceled_ = false;

    const wrappedPromise = new Promise((resolve, reject) => {
        promise.then(
            (val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
            (error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
        );
    });

    return {
        promise: wrappedPromise,
        cancel() {
            hasCanceled_ = true;
        },
    };
};

function handleErrors(response) {
    if (!response.ok) {
        throw Error(response.statusText);
    }
    return response;
}

export const fixedFetch = (url, cb, updateInSec = 30) => {
    let timeoutId = null;
    let isCanceled = false;

    if (!url) {
        throw new Error('You have to provide valid URL.');
    }

    if (typeof cb !== 'function') {
        throw new Error('You have to provide function as callback.');
    }

    const _fixedFetch = () => {
        fetch(url)
            .then(handleErrors)
            .then((response) => response.json())
            .then((data) => {
                if (!isCanceled) {
                    cb(data);
                }
            })
            .catch((error) => { throw new Error(error); })
            .finally(() => {
                if (!isCanceled) {
                    timeoutId = setTimeout(() =>
                        _fixedFetch(url, cb, updateInSec), updateInSec * 1000);
                }
            });
    };

    _fixedFetch();

    return () => {
        isCanceled = true;
        clearTimeout(timeoutId);
    };
};

export const getRoundById = (rounds, id) => {
    if (!Array.isArray(rounds) || !id) return null;

    const foundRound = rounds.find((round) => round.id.toString() === id.toString());

    return foundRound || null;
};

export const getPlayerSitecoreData = (sitecorePlayers, playerId) => {
    if (!sitecorePlayers || !Array.isArray(sitecorePlayers.players) || !playerId) return null;

    const sitecoreData = sitecorePlayers.players.find((sitecorePlayer) => {
        if (sitecorePlayer.playerId) {
            return sitecorePlayer.playerId.toString() === playerId.toString();
        }

        return false;
    });

    return sitecoreData || null;
};

export const getCustomEventPlayerSitecoreData = (sitecorePlayers, playerId) => {
    if (!sitecorePlayers || !Array.isArray(sitecorePlayers) || !playerId) return null;

    const sitecoreData = sitecorePlayers.find((sitecorePlayer) => {
        if (sitecorePlayer.scoringEnginePlayerId) {
            return sitecorePlayer.scoringEnginePlayerId.toString() === playerId.toString();
        }

        return false;
    });

    return sitecoreData || null;
};

export const getPlayerTeeTime = (currentRound, currentRoundStatus, playerRounds) => {
    const roundId = Number(currentRound);

    if (Number.isNaN(roundId) ||
        typeof currentRoundStatus !== 'number' ||
        !Array.isArray(playerRounds)
    ) {
        return null;
    }

    let teeTimeRound = roundId;

    if (currentRoundStatus === 5 || currentRoundStatus === 6) {
        if (roundId === 4) {
            return null;
        }

        teeTimeRound = roundId + 1;
    }

    const foundRound = playerRounds.find((round) =>
        round.id.toString() === teeTimeRound.toString());

    if (foundRound) {
        return foundRound.teeTime;
    }

    return null;
};

export const getCustomEventGroupTeeTimes = (scoringPlayerRound) => {
    if (scoringPlayerRound === null || scoringPlayerRound === undefined) return null;

    const playerHasntStartedYet = scoringPlayerRound.info.every((v) => v.playerPar === 0);
    if (!playerHasntStartedYet) return null;

    return scoringPlayerRound.teeTime;
};

export const isRoundChoicesEqual = (firstChoices, secondChoices) => {
    if (!Array.isArray(firstChoices) ||
        !Array.isArray(secondChoices)
    ) {
        return false;
    }

    return firstChoices.length === secondChoices.length &&
        !difference(
            map(firstChoices, (choice) => `${choice.value}-${choice.selected.toString()}`),
            map(secondChoices, (choice) => `${choice.value}-${choice.selected.toString()}`)
        ).length;
};

export const isRoundChoiceAvailable = (roundChoices, roundId) => {
    if (!Array.isArray(roundChoices) || !roundId) return false;

    return roundChoices.some((roudChoice) =>
        roudChoice.value === roundId.toString());
};

export const setSelectedChoice = (choices, value) => {
    if (!Array.isArray(choices) || !value) return;

    choices.forEach((choice) => {
        choice.selected = choice.value.toString() === value.toString();
    });
};

export const checkIfRoundHasTeeTimes = (players) => {
    const hasTeeTimesDisplayed = players.find((obj) => obj.hole === null);

    return hasTeeTimesDisplayed !== undefined;
};

export const updateTOCHUserMemberships = (membership) => {
    const memberships = userService.getDataFromLocalStorage(CookieNames.TOCH_USER_MEMBERSHIPS);

    if (memberships === null || memberships === undefined || memberships === '') return;

    const parsedMemberships = JSON.parse(memberships);
    const newMemberships = parsedMemberships + ';' + membership;
    userService.setTOCHUserMembershipsInLocalStorage(JSON.stringify(newMemberships));
};

export const getWebsiteDomain = () => {
    const isWomensOpen = window.location.hostname.toLowerCase().includes(siteConstants.womensOpen.toLowerCase());
    return isWomensOpen ? siteConstants.womensOpen : siteConstants.theOpen;
};

export const getLastLoginPlatform = () => {
    const isWomensOpen = window.location.hostname.toLowerCase().includes(siteConstants.womensOpen.toLowerCase());
    return isWomensOpen ? platformConstants.womensOpen : platformConstants.theOpen;
};
